import * as React from "react";

import {
  ScreenData,
  useNextUrlWithPendingAppInstallationId,
} from "@components/account/common";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { routes } from "@editor/utils/router";

import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { AnimatePresence, motion } from "framer-motion";
import { UserRound } from "lucide-react";
import {
  Navigate,
  Outlet,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useInterval } from "replo-utils/react/use-interval";

export function AuthLayout() {
  const { isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  const hasMarketingSiteSeedData = localStorage.getItem("replo_marketing_seed");
  const marketingSiteSeedData = hasMarketingSiteSeedData
    ? JSON.parse(hasMarketingSiteSeedData)
    : null;
  const marketingSitePrompt = marketingSiteSeedData
    ? marketingSiteSeedData.prompt
    : null;
  const hasMarketingSiteImage = marketingSiteSeedData
    ? marketingSiteSeedData.hasImage
    : false;
  const marketingSiteImage = hasMarketingSiteImage
    ? `https://marketing-uploads.replocdn.com/marketing-website-prompt-uploads/${marketingSiteSeedData.jti}`
    : "";

  if (isAuthenticated) {
    return (
      <Navigate
        to={{
          pathname: nextUrl ?? routes.home.root,
          search: nextSearchParams.toString(),
        }}
        replace
      />
    );
  }

  const renderData = ScreenData[location.pathname ?? ""];
  return (
    <div className="no-scrollbar flex w-full flex-col items-center justify-center gap-0 overflow-x-hidden overflow-y-scroll px-4 md:py-10 lg:h-screen lg:w-screen lg:flex-row lg:gap-0 lg:px-0 lg:py-0">
      <div className="order-2 flex h-full w-full max-w-[400px] flex-col items-center justify-center lg:order-1 lg:w-1/2 lg:max-w-none lg:items-stretch lg:p-0">
        {hasMarketingSiteSeedData ? (
          <ReploChatPreview
            marketingSitePrompt={marketingSitePrompt}
            marketingSiteImage={marketingSiteImage}
          />
        ) : (
          <FeaturedCaseStudy />
        )}
      </div>
      <div className="order-1 my-4 flex w-full items-center justify-center lg:order-2 lg:my-0 lg:w-1/2">
        <div className="mx-auto flex w-full flex-col items-center gap-6 sm:h-full sm:max-w-[370px] md:mx-0 md:justify-center">
          <div className="flex flex-col gap-4 items-center">
            <Tag size="base" variant="info">
              ★ 4.7 | 91,000+ users
            </Tag>
            <div className="text-4xl text-center font-bold font-editorial">
              Sell{" "}
              <span className="font-editorial font-light italic">Anything</span>{" "}
              with Replo
            </div>
          </div>
          <div className="flex w-full flex-col gap-3">
            <Outlet />
            <div className="text-placeholder text-base">
              {renderData?.footerText}
              <span
                className="text-primary cursor-pointer pl-2"
                onClick={() => {
                  const query = getQueryDefaultsFromLocation(searchParams);
                  if (renderData?.footerCtaRedirectTo) {
                    // Note (Sebas, 2023-06-30): This is the place where we set the next
                    // parameter for the login/signup flow to prevent losing the value.
                    navigate(
                      // Note (Noah, 2024-02-14): Preserve query strings in this navigate call, since
                      // we have query params like shopifyShopURL and pendingInstallationId we need to
                      // preserve in order to make the claim flow work if they just came from installing
                      // the shopify integration
                      {
                        pathname: renderData.footerCtaRedirectTo,
                        search: searchParams.toString(),
                      },
                      {
                        state: { next: query.next ?? null },
                      },
                    );
                  }
                }}
              >
                {renderData?.footerCtaText}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const brands = {
  possible: {
    logo: "/images/logos/possible-logo.svg",
    icon: "/images/logos/possible.svg",
    text: "increased",
    italicText: "revenue per visitor",
    endText: "by",
    metric: "10.25%",
  },
  tushy: {
    logo: "/images/logos/tushy-logo.svg",
    icon: "/images/logos/tushy.svg",
    text: "improved",
    italicText: "conversion rates",
    endText: "by",
    metric: "over 200%",
  },
  simplemodern: {
    logo: "/images/logos/simplemodern-logo.svg",
    icon: "/images/logos/simplemodern.svg",
    text: "increased",
    italicText: "conversions",
    endText: "by",
    metric: "51.7%",
  },
  woxer: {
    logo: "/images/logos/woxer-logo.svg",
    icon: "/images/logos/woxer.svg",
    text: "decreased",
    italicText: "CPA's",
    endText: "on ads by",
    metric: "$10.35",
  },
  loop: {
    logo: "/images/logos/loop-logo.svg",
    icon: "/images/logos/loop.svg",
    text: "tested and",
    italicText: "launched",
    endText: "",
    metric: "40+ campaigns",
  },
  xlr: {
    logo: "/images/logos/xlr-logo.svg",
    icon: "/images/logos/xlr.svg",
    text: "scaled",
    italicText: "revenue",
    endText: "by",
    metric: "343%",
  },
  jibby: {
    logo: "/images/logos/jibby-logo.svg",
    icon: "/images/logos/jibby.svg",
    text: "propelled",
    italicText: "site conversion rates",
    endText: "to",
    metric: "6.4%",
  },
  jot: {
    logo: "/images/logos/jot-logo.svg",
    icon: "/images/logos/jot.svg",
    text: "boosted",
    italicText: "website conversion rates",
    endText: "by",
    metric: "20.1%",
  },
  bonlook: {
    logo: "/images/logos/bonlook-logo.svg",
    icon: "/images/logos/bonlook.svg",
    text: "raised",
    italicText: "baseline revenue",
    endText: "by",
    metric: "27%",
  },
} as const;

type BrandKey = keyof typeof brands;
const brandKeys = Object.keys(brands) as BrandKey[];

function FeaturedCaseStudy() {
  const [hoveredBrand, setHoveredBrand] = React.useState<BrandKey | null>(null);
  const [activeBrand, setActiveBrand] = React.useState<BrandKey>("possible");
  const currentBrand: BrandKey = hoveredBrand ?? activeBrand;

  useInterval(() => {
    setActiveBrand((prev: BrandKey) => {
      const currentIndex = brandKeys.indexOf(prev);
      const nextIndex = (currentIndex + 1) % brandKeys.length;
      return brandKeys[nextIndex] ?? prev;
    });
  }, 5000);

  const handleBrandHover = (brand: BrandKey) => {
    setHoveredBrand(brand);
  };

  const handleBrandHoverEnd = () => {
    if (hoveredBrand) {
      setActiveBrand(hoveredBrand);
    }
    setHoveredBrand(null);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap');
        
        @font-face {
          font-family: 'PP Editorial New';
          src: url('https://assets.replocdn.com/PPEditorialNew-Regular.woff2') format('woff2');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        
        @font-face {
          font-family: 'PP Editorial New';
          src: url('https://assets.replocdn.com/PPEditorialNew-Italic.woff2') format('woff2');
          font-weight: normal;
          font-style: italic;
          font-display: swap;
        }
      `}</style>
      <div className="px-16 py-40 hidden h-full w-full overflow-hidden bg-black lg:block">
        <motion.div className="flex flex-col items-center gap-6" layout>
          <div className="h-16 w-48 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentBrand}
                src={brands[currentBrand].logo}
                alt={currentBrand}
                className="max-h-full max-w-full object-contain"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              />
            </AnimatePresence>
          </div>
          <motion.div className="flex flex-col items-center gap-6" layout>
            <AnimatePresence mode="wait">
              <motion.div
                key={`headline-${currentBrand}`}
                className="text-5xl tracking-wide text-seashell font-editorial text-center"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                layout
              >
                {brands[currentBrand].text}{" "}
                <span className="italic">
                  {brands[currentBrand].italicText}
                </span>{" "}
                {brands[currentBrand].endText}
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              <motion.div
                key={`metric-${currentBrand}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                layout
              >
                <div className="px-4 transform -skew-x-30 bg-seashell">
                  <div className="text-4xl font-bold transform skew-x-30 text-black font-archivo">
                    {brands[currentBrand].metric}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center gap-4">
              <span className="text-4xl text-seashell font-editorial">
                with
              </span>
              <img src="/images/logos/replo-light.svg" alt="Replo" />
            </div>
          </motion.div>
        </motion.div>
        <div className="w-full py-15">
          <div className="h-px bg-seashell"></div>
        </div>
        <div className="flex flex-col items-center gap-10">
          <div className="font-archivo font-light text-base text-seashell">
            Trusted by 91,000+ teams to sell more of everything
          </div>
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center justify-center gap-8 flex-wrap">
              {brandKeys.slice(0, 4).map((brandKey) => (
                <img
                  key={brandKey}
                  src={brands[brandKey].icon}
                  alt={brandKey.charAt(0).toUpperCase() + brandKey.slice(1)}
                  className={twMerge(
                    "opacity-50 hover:opacity-100 transition-opacity",
                    currentBrand === brandKey && "opacity-100",
                  )}
                  onMouseEnter={() => handleBrandHover(brandKey)}
                  onMouseLeave={handleBrandHoverEnd}
                />
              ))}
            </div>
            <div className="flex items-center justify-center gap-8 flex-wrap">
              {brandKeys.slice(4).map((brandKey) => (
                <img
                  key={brandKey}
                  src={brands[brandKey].icon}
                  alt={brandKey.charAt(0).toUpperCase() + brandKey.slice(1)}
                  className={twMerge(
                    "opacity-50 hover:opacity-100 transition-opacity",
                    currentBrand === brandKey && "opacity-100",
                  )}
                  onMouseEnter={() => handleBrandHover(brandKey)}
                  onMouseLeave={handleBrandHoverEnd}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ReploChatPreview({
  marketingSitePrompt,
  marketingSiteImage,
}: {
  marketingSitePrompt: string;
  marketingSiteImage: string;
}) {
  return (
    <div className="px-25 py-30 hidden h-full w-full overflow-hidden bg-black bg-[url('/images/onboarding/gradient-background.png')] bg-cover bg-center lg:block">
      <div
        className={twMerge(
          "flex flex-col items-center gap-6",
          marketingSiteImage ? "pt-20" : "pt-30",
        )}
      >
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 bg-[url('/images/onboarding/replo-logo-green-background.svg')] bg-cover bg-center flex-shrink-0" />
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="text-[#F5FAD9] text-lg font-light">Replo AI</div>
              <div className="border border-[#F5FAD933] bg-[#1E1E1EE5] text-[#F5FAD9] text-lg font-light rounded-lg p-2 w-full">
                What can I help you build?
              </div>
            </div>
            <div className="w-10 h-10 flex-shrink-0"></div>
          </div>
        </div>
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 flex-shrink-0"></div>
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="flex items-end justify-between">
                <div className="text-[#F5FAD9] text-lg font-light">You</div>
                {marketingSiteImage && (
                  <div className="w-20 h-20">
                    <img
                      src={marketingSiteImage}
                      alt="Marketing site reference"
                      className="w-full h-full object-cover rounded-lg border border-[#F5FAD933]"
                    />
                  </div>
                )}
              </div>
              <div className="border border-[#F5FAD933] bg-white text-black text-lg font-light rounded-lg p-2 w-full">
                {marketingSitePrompt}
              </div>
            </div>
            <div className="w-10 h-10 bg-[#1E1E1EE5] flex items-center justify-center rounded flex-shrink-0 border border-[#F5FAD933] rounded-lg">
              <UserRound className="w-5 h-5 text-[#F5FAD9]" />
            </div>
          </div>
        </div>
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 bg-[url('/images/onboarding/replo-logo-green-background.svg')] bg-cover bg-center flex-shrink-0" />
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="text-[#F5FAD9] text-lg font-light">Replo AI</div>
              <div className="border border-[#F5FAD933] bg-[#1E1E1EE5] text-[#F5FAD9] text-lg font-light rounded-lg p-2 w-full">
                Great idea! Please sign up to start building.
              </div>
            </div>
            <div className="w-10 h-10 flex-shrink-0"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * We persist these between SIGNUP/LOGIN routes so that when someone installs the app
 * but already has an account, we can link the shopify URL to the correct account.
 */
function getQueryDefaultsFromLocation(searchParams: URLSearchParams) {
  const query: Record<string, string> = {};
  for (const key of [
    "email",
    "first_name",
    "last_name",
    "shopify_shop_url",
    "nonce",
    "next",
  ]) {
    const value = searchParams.get(key);
    if (value) {
      query[key] = value;
    }
  }
  return query;
}
